import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { WeightScaleService } from '../../../../Services/WeightScale/weight-scale.service';
import { NotificationService } from '../../../../Services/notification.service';
import { Subscription, interval } from 'rxjs';

@Component({
  selector: 'app-weighing-scale-setting',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './weighing-scale-setting.component.html',
  styleUrls: ['./weighing-scale-setting.component.css']
})
export class WeighingScaleSettingComponent implements OnInit, OnDestroy {
  settingsForm!: FormGroup;
  agentStatus: 'Online' | 'Offline' = 'Offline';
  agentDetails: any = null;
  detectedPorts: any[] = [];
  ownerId: number = 1;
  savedComPort: string = '';
  
  // Simulated / Mock weight controls
  mockWeightValue: number = 0.0;
  isMockWeightActive: boolean = false;
  liveAgentWeight: number = 0.0;
  
  private pollingSub?: Subscription;

  constructor(
    private fb: FormBuilder,
    private weightScaleService: WeightScaleService,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadOwnerId();
    this.loadSettings();
    this.scanLocalPorts(true);
    this.startAgentStatusPolling();
  }

  ngOnDestroy(): void {
    if (this.pollingSub) {
      this.pollingSub.unsubscribe();
    }
  }

  initForm(): void {
    this.settingsForm = this.fb.group({
      enabled: [false],
      com_port: ['BROWSER_DIRECT', [Validators.required]],
      start_string: [''],
      end_string: [''],
      data_rate: [9600, [Validators.required, Validators.min(300)]],
      calibration: [1, [Validators.required, Validators.min(1)]]
    });

    this.settingsForm.get('enabled')?.valueChanges.subscribe((isEnabled) => {
      this.autoSaveEnabledState(isEnabled);
    });

  }

  loadOwnerId(): void {
    try {
      const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        // resolve user owner_id
        this.ownerId = user.owner_id || user.own_id || user.id || 1;
      }
    } catch (e) {
      console.error('Error loading owner info:', e);
    }
  }

  loadSettings(): void {
    this.weightScaleService.getSettings().subscribe({
      next: (res: any) => {
        if (res && res.data) {
          const s = res.data;
          this.savedComPort = s.com_port || 'BROWSER_DIRECT';
          
           this.cdr.detectChanges();
          
          setTimeout(() => {
            this.settingsForm.patchValue({
              enabled: !!s.enabled,
              com_port: this.savedComPort,
              start_string: s.start_string || '',
              end_string: s.end_string || '',
              data_rate: parseInt(s.data_rate) || 9600,
              calibration: parseInt(s.calibration) || 1
            }, { emitEvent: false });
            this.cdr.detectChanges();
          }, 0);
          
          if (s.com_port === 'BROWSER_DIRECT') {
            if (s.enabled) {
              this.weightScaleService.autoConnectDirectBrowserSerial(
                parseInt(s.data_rate) || 9600,
                parseInt(s.calibration) || 1,
                s.start_string || '',
                s.end_string || ''
              ).then(success => {
                if (success) {
                  console.log('Connected to browser serial port automatically.');
                }
                this.checkLocalAgentStatus();
              });
            }
          } else {
            // Trigger local agent initialization only if enabled
            if (s.enabled) {
              this.initializeLocalAgent();
            }
          }
        }
      },
      error: () => {
        this.notificationService.showError('Failed to load weighing scale settings', 'Error');
      }
    });
  }

  initializeLocalAgent(): void {
    this.weightScaleService.initializeLocalAgent(this.ownerId).subscribe({
      next: (res) => {
        if (res && res.success) {
          console.log('Local hardware agent initialized successfully.');
          this.checkLocalAgentStatus();
          this.scanLocalPorts(true);
        }
      }
    });
  }

  checkLocalAgentStatus(): void {
    const isEnabled = this.settingsForm?.get('enabled')?.value;
    if (!isEnabled) {
      this.agentStatus = 'Offline';
      this.agentDetails = null;
      return;
    }

    const isDirect = this.settingsForm?.get('com_port')?.value === 'BROWSER_DIRECT';
    if (isDirect) {
      const isConnected = this.weightScaleService.isDirectBrowserConnected();
      this.agentStatus = isConnected ? 'Online' : 'Offline';
      this.liveAgentWeight = this.weightScaleService.getDirectLatestWeight();
      this.isMockWeightActive = false;
      this.agentDetails = {
        com_port: this.weightScaleService.getDirectDeviceName(),
        enabled: this.settingsForm?.get('enabled')?.value,
        data_rate: this.settingsForm?.get('data_rate')?.value,
        calibration: this.settingsForm?.get('calibration')?.value
      };
      return;
    }

    this.weightScaleService.getLocalAgentStatus().subscribe({
      next: (res) => {
        if (res && res.initialized) {
          this.agentStatus = 'Online';
          this.agentDetails = res.config;
          this.liveAgentWeight = res.latest_weight;
          this.isMockWeightActive = res.mock_weight_active;
        } else {
          this.agentStatus = 'Offline';
          this.agentDetails = null;
        }
      },
      error: () => {
        this.agentStatus = 'Offline';
        this.agentDetails = null;
      }
    });
  }

  startAgentStatusPolling(): void {
    // Poll agent status & live weight every 2 seconds
    this.pollingSub = interval(2000).subscribe(() => {
      this.checkLocalAgentStatus();
    });
  }

  scanLocalPorts(restoreSaved: boolean = false): void {
    const currentValue = this.settingsForm?.get('com_port')?.value;
    this.weightScaleService.autoDetectLocalPorts().subscribe({
      next: (res) => {
        if (res && res.success && res.ports) {
          this.detectedPorts = res.ports;
          this.cdr.detectChanges();
          setTimeout(() => {
            this.settingsForm.patchValue({
              com_port: restoreSaved ? this.savedComPort : currentValue
            }, { emitEvent: false });
            this.cdr.detectChanges();
          }, 0);
        }
      }
    });
  }

  async connectDirectly(): Promise<void> {
    const baudRate = this.settingsForm.get('data_rate')?.value || 9600;
    const divisor = this.settingsForm.get('calibration')?.value || 1;
    const startString = this.settingsForm.get('start_string')?.value || '';
    const endString = this.settingsForm.get('end_string')?.value || '';
    
    const connected = await this.weightScaleService.requestDirectBrowserSerial(baudRate, divisor, startString, endString);
    if (connected) {
      this.notificationService.showSuccess('Weighing scale connected directly inside browser successfully!', 'Success');
      
      // Patch the form values to BROWSER_DIRECT and enabled to true
      this.settingsForm.patchValue({
        com_port: 'BROWSER_DIRECT',
        enabled: true
      }, { emitEvent: false });
      
      // Auto-save this direct configuration to the database so it persists for the sales/billing panel
      const payload = {
        ...this.settingsForm.value,
        com_port: 'BROWSER_DIRECT',
        enabled: true
      };
      
      this.weightScaleService.saveSettings(payload).subscribe({
        next: () => {
          this.savedComPort = 'BROWSER_DIRECT';
          console.log('Saved BROWSER_DIRECT mode to database.');
          this.checkLocalAgentStatus();
        },
        error: (err) => {
          console.error('Failed to save BROWSER_DIRECT settings to backend:', err);
          this.checkLocalAgentStatus();
        }
      });
    } else {
      this.notificationService.showError('Failed to connect to scale. Please check connection and try again.', 'Error');
    }
  }

  setMockWeight(): void {
    const val = this.isMockWeightActive ? this.mockWeightValue : null;
    this.weightScaleService.setMockWeight(val).subscribe({
      next: (res) => {
        if (res.success) {
          this.notificationService.showSuccess(
            this.isMockWeightActive ? `Mock weight set to ${this.mockWeightValue}g` : 'Mock weight disabled. Simulated mode active.',
            'Success'
          );
        } else {
          this.notificationService.showError('Could not set mock weight. Is agent running?', 'Error');
        }
      }
    });
  }

  toggleMockWeightMode(): void {
    this.isMockWeightActive = !this.isMockWeightActive;
    this.setMockWeight();
  }

  onSubmit(): void {
    if (this.settingsForm.invalid) {
      this.notificationService.showError('Please correct validation errors', 'Error');
      return;
    }

    const payload = this.settingsForm.value;
    this.weightScaleService.saveSettings(payload).subscribe({
      next: () => {
        this.notificationService.showSuccess('Weighing scale settings saved successfully', 'Success');
        this.savedComPort = payload.com_port;
        if (payload.com_port === 'BROWSER_DIRECT') {
          if (payload.enabled) {
            this.weightScaleService.autoConnectDirectBrowserSerial(
              parseInt(payload.data_rate) || 9600,
              parseInt(payload.calibration) || 1,
              payload.start_string || '',
              payload.end_string || ''
            );
          } else {
            this.weightScaleService.closeDirectBrowserSerial();
          }
        } else {
          // Close direct serial if switching back to agent mode
          this.weightScaleService.closeDirectBrowserSerial();
          // Re-initialize agent with new settings only if enabled
          if (payload.enabled) {
            this.initializeLocalAgent();
          }
        }
      },
      error: () => {
        this.notificationService.showError('Failed to save settings to server', 'Error');
      }
    });
  }

  autoSaveEnabledState(isEnabled: boolean): void {
    const payload = {
      ...this.settingsForm.value,
      enabled: isEnabled
    };

    this.weightScaleService.saveSettings(payload).subscribe({
      next: () => {
        this.notificationService.showSuccess(
          isEnabled ? 'Weighing scale enabled successfully' : 'Weighing scale disabled successfully',
          'Success'
        );
        this.savedComPort = payload.com_port;
        if (payload.com_port === 'BROWSER_DIRECT') {
          if (isEnabled) {
            this.weightScaleService.autoConnectDirectBrowserSerial(
              parseInt(payload.data_rate) || 9600,
              parseInt(payload.calibration) || 1,
              payload.start_string || '',
              payload.end_string || ''
            );
          } else {
            this.weightScaleService.closeDirectBrowserSerial();
          }
        } else {
          this.weightScaleService.closeDirectBrowserSerial();
          if (isEnabled) {
            this.initializeLocalAgent();
          }
        }
      },
      error: () => {
        this.notificationService.showError('Failed to update weighing scale status', 'Error');
      }
    });
  }

  isCurrentPortNotPresent(): boolean {
    const currentPort = this.savedComPort;
    if (!currentPort || currentPort === 'BROWSER_DIRECT' || currentPort === 'SIMULATE') {
      return false;
    }
    return !this.detectedPorts.some(port => port.path === currentPort);
  }
}
